import {
  Alert,
  Button,
  DataTable,
  FormField,
  Input,
  Section,
  Spinner,
  Stack,
  Switch,
  useToast,
} from '@ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../../lib/errors';
import { PushDevices } from '../../pwa/PushDevices';
import {
  useNotificationPreferences,
  useSaveNotificationPreferences,
  type NotificationPreferences,
  type NotificationSetting,
} from '../api';
import styles from './TasksPages.module.css';

type Channel = NotificationSetting['channel'];
type Kind = NotificationSetting['kind'];

const KINDS: Kind[] = ['reminder', 'assignment', 'digest'];
const CHANNELS: Channel[] = ['email', 'push'];

/** Which notifications go out on which channel (NOT-003, NOT-005). */
export function NotificationsPage() {
  const { t } = useTranslation();
  const prefs = useNotificationPreferences();
  if (!prefs.data) {
    return prefs.error ? (
      <Alert tone="danger">{errorMessage(prefs.error)}</Alert>
    ) : (
      <Spinner label={t('common.loading')} />
    );
  }
  return (
    <>
      <PreferencesForm key={prefs.data.version} prefs={prefs.data} />
      <PushDevices />
    </>
  );
}

function PreferencesForm({ prefs }: { prefs: NotificationPreferences }) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveNotificationPreferences();
  const [enabled, setEnabled] = useState(
    new Set(prefs.enabled.map((e) => `${e.channel}:${e.kind}`)),
  );
  const [time, setTime] = useState(prefs.digest_time.slice(0, 5));
  const toggle = (key: string, on: boolean) =>
    setEnabled((s) => {
      const next = new Set(s);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  const digest = CHANNELS.some((c) => enabled.has(`${c}:digest`));
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        version: prefs.version,
        digest_time: `${time || '07:00'}:00`,
        enabled: [...enabled].map((k) => {
          const [channel, kind] = k.split(':') as [Channel, Kind];
          return { channel, kind };
        }),
      },
      { onSuccess: () => toast.show(t('common.saved')) },
    );
  };

  // The matrix is the same table as everywhere (#177, #197).
  return (
    <form onSubmit={onSubmit} noValidate className={styles.stack}>
      <Section title={t('notifications.title')} variant="flush">
        <div className={styles.matrix}>
          <DataTable
            caption={t('notifications.kind')}
            layout="table"
            rows={KINDS}
            rowKey={(k) => k}
            columns={[
              {
                key: 'kind',
                header: t('notifications.kind'),
                hideHeader: true,
                cell: (k: Kind) => t(`notifications.kinds.${k}.title`),
              },
              ...CHANNELS.map((c) => ({
                key: c,
                header: t(`notifications.channel.${c}`),
                align: 'center' as const,
                width: '6rem',
                cell: (k: Kind) => (
                  <Switch
                    aria-label={`${t(`notifications.kinds.${k}.title`)}: ${t(`notifications.channel.${c}`)}`}
                    checked={enabled.has(`${c}:${k}`)}
                    onChange={(e) => toggle(`${c}:${k}`, e.target.checked)}
                  />
                ),
              })),
            ]}
          />
        </div>
      </Section>
      <Section variant="content">
        <Stack gap={5}>
          {save.error && <Alert tone="danger">{errorMessage(save.error)}</Alert>}
          <div className={styles.time}>
            <FormField label={t('notifications.digestTime')} hint={t('notifications.digestHint')}>
              {(p) => (
                <Input
                  {...p}
                  type="time"
                  value={time}
                  disabled={!digest}
                  onChange={(e) => setTime(e.target.value)}
                />
              )}
            </FormField>
          </div>
          <div>
            <Button type="submit" loading={save.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </Stack>
      </Section>
    </form>
  );
}
